// A frame's depth edges against its photo's (Match edges): the same camera, so a straight depth jump or crease should
// lie on a straight photo edge. Each depth segment and each photo segment parallel to it (within 5°), overlapping
// it and within `search` px across, give an offset across the depth segment; the frame's shift (dx, dy) is the
// one that puts the most depth segments on a photo edge, refined by least squares (depth and photo out of register:
// a calibration or timing error, or the depth's smoothing), and a depth segment is confirmed when a photo segment
// lies within tol of it once shifted. Pure.
import type { FrameFeats } from './feats.ts';
import type { Segs } from './segments.ts';

export interface EdgeMatchOpts { tol: number; search: number; kind: 'any' | 'jump' | 'crease' }
export interface EdgeMatch {
  /** Where the photo's edges sit from the depth's (photo px; the depth is off by minus this), and how well it is known (px). */
  shift: [number, number]; sd: number;
  /** The shift is known both ways (3 or more confirmed pairs, not all one direction). */
  ok: boolean;
  /** Pairs found within search, and confirmed (within tol once shifted). */
  pairs: number; confirmed: number;
  /** Rms across, of the confirmed pairs once shifted (px). */
  rms: number;
  /** Per depth segment (jump ones, then crease ones), 1 when confirmed. */
  jump: Uint8Array; crease: Uint8Array;
  /** Per photo segment, 1 when a depth segment agrees with it. */
  agreed: Uint8Array;
}

interface Cand { kind: 0 | 1; d: number; p: number; n: [number, number]; off: number }

/** Depth and photo edges of one frame matched (features from feats.ts, depth segments in photo pixels). */
export function matchEdges(ff: FrameFeats, o: EdgeMatchOpts): EdgeMatch {
  const P = ff.segs, cos5 = Math.cos((5 * Math.PI) / 180), cand: Cand[] = [];
  const kinds: [0 | 1, Segs][] = [];
  if (o.kind !== 'crease') kinds.push([0, ff.depth.jump]);
  if (o.kind !== 'jump') kinds.push([1, ff.depth.crease]);
  if (P) for (const [kind, D] of kinds) for (let d = 0; d < D.n; d++) {
    const a = D.seg.subarray(4 * d, 4 * d + 4), ux = a[2] - a[0], uy = a[3] - a[1], L = Math.hypot(ux, uy);
    if (L < 1e-6) continue;
    const ex = ux / L, ey = uy / L, n: [number, number] = [ey, -ex];
    for (let p = 0; p < P.n; p++) {
      const b = P.seg.subarray(4 * p, 4 * p + 4), vx = b[2] - b[0], vy = b[3] - b[1], M = Math.hypot(vx, vy);
      if (Math.abs(ex * vx + ey * vy) / M < cos5) continue;   // either way round: polarity doesn't carry over
      const mx = (b[0] + b[2]) / 2 - a[0], my = (b[1] + b[3]) / 2 - a[1], off = n[0] * mx + n[1] * my;
      if (Math.abs(off) > o.search) continue;
      const t0 = (b[0] - a[0]) * ex + (b[1] - a[1]) * ey, t1 = (b[2] - a[0]) * ex + (b[3] - a[1]) * ey;
      if (Math.min(L, Math.max(t0, t1)) - Math.max(0, Math.min(t0, t1)) < 0.5 * Math.min(L, M)) continue;
      cand.push({ kind, d, p, n, off });
    }
  }
  // the shift: first the one (on a tol-spaced grid over ±search) that puts the most depth segments on a photo edge
  // (one each: texture parallel to an edge nearby, or a depth edge with no photo edge, mustn't count), then refined
  // by least squares over each depth segment's nearest pair, Cauchy-weighted
  const segKey = (q: Cand) => q.kind * 1e6 + q.d, byD = new Map<number, Cand[]>();
  for (const q of cand) (byD.get(segKey(q)) ?? byD.set(segKey(q), []).get(segKey(q))!).push(q);
  const groups = [...byD.values()];
  const score = (sx: number, sy: number) => { let n = 0; for (const g of groups) if (g.some(q => Math.abs(q.off - (q.n[0] * sx + q.n[1] * sy)) <= o.tol)) n++; return n; };
  let s: [number, number] = [0, 0], top = score(0, 0);
  const step = Math.max(0.5, o.tol), m = Math.floor(o.search / step);
  for (let i = -m; i <= m; i++) for (let j = -m; j <= m; j++) {
    const n = score(i * step, j * step);
    if (n > top || (n === top && Math.hypot(i, j) * step < Math.hypot(...s))) { top = n; s = [i * step, j * step]; }
  }
  for (let it = 0; it < 10 && groups.length; it++) {
    let a = 1e-6, b = 0, e = 1e-6, g0 = 0, g1 = 0;
    for (const g of groups) {
      let q = g[0], rq = Infinity;
      for (const x of g) { const r = Math.abs(x.off - (x.n[0] * s[0] + x.n[1] * s[1])); if (r < rq) { rq = r; q = x; } }
      const w = 1 / (1 + (rq / o.tol) ** 2);
      a += w * q.n[0] * q.n[0]; b += w * q.n[0] * q.n[1]; e += w * q.n[1] * q.n[1]; g0 += w * q.n[0] * q.off; g1 += w * q.n[1] * q.off;
    }
    const det = a * e - b * b;
    if (Math.abs(det) < 1e-9) break;   // all one direction: the other component stays where the search put it
    s = [(e * g0 - b * g1) / det, (a * g1 - b * g0) / det];
  }
  const jump = new Uint8Array(ff.depth.jump.n), crease = new Uint8Array(ff.depth.crease.n), agreed = new Uint8Array(P?.n ?? 0);
  let ss = 0, k = 0;
  const best = new Map<string, number>();
  for (const q of cand) {
    const r = Math.abs(q.off - (q.n[0] * s[0] + q.n[1] * s[1]));
    if (r > o.tol) continue;
    const key = `${q.kind}:${q.d}`;
    if ((best.get(key) ?? Infinity) > r) best.set(key, r);
    (q.kind ? crease : jump)[q.d] = 1;
    agreed[q.p] = 1;
  }
  for (const r of best.values()) { ss += r * r; k++; }
  // known both ways: the confirmed pairs' normals spread over both directions (the smaller eigenvalue of Σ n nᵀ)
  let a = 0, b = 0, e = 0;
  for (const q of cand) if (Math.abs(q.off - (q.n[0] * s[0] + q.n[1] * s[1])) <= o.tol) { a += q.n[0] ** 2; b += q.n[0] * q.n[1]; e += q.n[1] ** 2; }
  const lmin = (a + e) / 2 - Math.sqrt(((a - e) / 2) ** 2 + b * b);
  return { ok: k >= 3 && lmin >= 1.5, shift: s, sd: k ? Math.sqrt(ss / k) / Math.sqrt(k) : NaN, pairs: cand.length, confirmed: k, rms: k ? Math.sqrt(ss / k) : NaN, jump, crease, agreed };
}

/** The confirmed depth segments of a match, packed x0 y0 x1 y1 (photo px, where the depth has them). */
export function confirmedSegs(ff: FrameFeats, m: EdgeMatch): Float32Array {
  const out: number[] = [];
  for (const [D, c] of [[ff.depth.jump, m.jump], [ff.depth.crease, m.crease]] as const) for (let i = 0; i < D.n; i++) if (c[i]) out.push(...D.seg.subarray(4 * i, 4 * i + 4));
  return Float32Array.from(out);
}
